/* Email → Templates: a gallery of every email StartHR sends, previewed with sample data in English, Arabic or both. */
import { useState } from "react";
import { useEmailPreview, useEmailTemplates } from "../../../data/hooks";
import type { EmailTemplateInfo, Lang } from "../../../data/types";
import { useI18n } from "../../../i18n";
import { Card, Chip, Grid, Loaded, Seg } from "../../../ui";

export const CATEGORY_L: Record<string, [string, string]> = {
  account: ["Account & security", "الحساب والأمان"], billing: ["Billing", "الفوترة"], hr: ["HR", "الموارد البشرية"], compliance: ["Compliance", "الامتثال"],
  devices: ["Devices", "الأجهزة"], test: ["Test", "تجربة"],
};
const clean = (s: string) => s.replace(/\{\{[^}]*\}\}/g, "…");

function Preview({ k }: { k: string }) {
  const { L } = useI18n();
  const [lang, setLang] = useState<Lang | "bi">("en");
  const [view, setView] = useState<"html" | "text">("html");
  const q = useEmailPreview(k, lang);
  return (
    <Card title={L("Preview", "معاينة")} testId="tpl-preview" actions={<Seg label={L("Language", "اللغة")} value={lang} onChange={setLang} items={[["en", "EN"], ["ar", "ع"], ["bi", "EN + ع"]]} />}>
      <Loaded q={q}>{(p) => (
        <div className="stack">
          <div>
            <b className="sm" dir="auto" data-testid="tpl-subject">{p.subject}</b>
            {p.preheader && <div className="xs muted" dir="auto">{p.preheader}</div>}
          </div>
          <Seg label={L("Format", "الصيغة")} value={view} onChange={setView} items={[["html", "HTML"], ["text", L("Plain text", "نص عادي")]]} />
          {view === "html"
            ? <iframe className="email-frame" title={p.subject} sandbox="" srcDoc={p.html} data-testid="tpl-frame" />
            : <pre className="code-block" style={{ whiteSpace: "pre-wrap", maxHeight: 560 }} dir="auto" data-testid="tpl-text">{p.text}</pre>}
        </div>
      )}</Loaded>
    </Card>
  );
}

export default function TemplatesTab() {
  const { L } = useI18n();
  const q = useEmailTemplates();
  const [sel, setSel] = useState("welcome");
  return (
    <Loaded q={q}>{(list) => {
      const groups = list.reduce<Record<string, EmailTemplateInfo[]>>((a, t) => ((a[t.category] ??= []).push(t), a), {});
      const cur = list.some((t) => t.key === sel) ? sel : list[0]?.key ?? "";
      return (
        <Grid cols="1-2">
          <Card flush title={L("Templates", "القوالب")} sub={list.length + " " + L("emails", "رسالة")} testId="tpl-list">
            {Object.entries(groups).map(([cat, items]) => (
              <div key={cat}>
                <div className="xs muted" style={{ padding: "10px 16px 4px", fontWeight: 600 }}>{L(...(CATEGORY_L[cat] ?? [cat, cat]))}</div>
                {items.map((t) => (
                  <button key={t.key} type="button" className={"list-row" + (t.key === cur ? " on" : "")} aria-pressed={t.key === cur} onClick={() => setSel(t.key)} data-testid={"tpl-" + t.key}
                    style={{ width: "100%", border: 0, background: t.key === cur ? "var(--primary-soft)" : "transparent", cursor: "pointer", textAlign: "start", font: "inherit", color: "inherit", padding: "8px 16px" }}>
                    <span className="grow" style={{ minWidth: 0 }}>
                      <b className="sm" style={{ display: "block" }} dir="auto">{clean(L(t.subject.en, t.subject.ar))}</b>
                      <code className="xs muted">{t.key}</code>
                    </span>
                    {t.essential ? <Chip nodot tone="pri">{L("Always sent", "تُرسل دائمًا")}</Chip> : <Chip nodot>{L("Opt-out", "قابلة للإيقاف")}</Chip>}
                  </button>
                ))}
              </div>
            ))}
          </Card>
          {cur && <Preview key={cur} k={cur} />}
        </Grid>
      );
    }}</Loaded>
  );
}
